import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { changelog as api } from '@/api/endpoints'
import { Alert, Modal, Spinner } from '@/components/ui'
import { fieldLabelKey, formatValue } from '@/features/changelog/fields'
import { useBootstrap } from '@/hooks/useBootstrap'
import { formatDateTime } from '@/lib/utils'
import type { ChangeKind } from '@/types/api'

/** Ako sa volá ten, koho sa zmena týka - podľa druhu záznamu. */
const SUBJECT_LABEL: Record<ChangeKind, string> = {
  'client-reservations': 'changelog.subjectClient',
  'instructor-reservations': 'changelog.subjectInstructor',
  clients: 'changelog.subjectClient',
  instructors: 'changelog.subjectInstructor',
}

/** Ktoré polia nesú id do číselníka, ktorý má bootstrap. */
const CODEBOOK: Record<string, 'statuses' | 'languages' | 'courses' | 'levels'> = {
  status_id: 'statuses',
  statusid: 'statuses',
  lang_id: 'languages',
  course_id: 'courses',
  levelid: 'levels',
}

/**
 * Potvrdenie vrátenia zmeny.
 *
 * Náhľad sa ťahá zo servera, nie skladá v prehliadači: rozhodnutie, čo sa dá
 * vrátiť a čo to spraví s cenou, patrí na jedno miesto — inak by sa okno
 * a skutočnosť rozišli.
 */
export default function RevertModal({
  kind,
  rowId,
  onClose,
  onReverted,
}: {
  kind: ChangeKind
  rowId: number
  onClose: () => void
  /** `newRowId` je riadok, ktorý vrátenie samo zanechalo v histórii - viď
   *  `UndoRedoControl` v kalendári, ktorý ho použije na Redo. */
  onReverted: (message: string, newRowId: number | null) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: boot } = useBootstrap()

  const { data: plan, isLoading } = useQuery({
    queryKey: ['revert-plan', kind, rowId],
    queryFn: () => api.revertPlan(kind, rowId),
  })

  const revert = useMutation({
    mutationFn: () => api.revert(kind, rowId),
    onSuccess: async (result) => {
      // Vrátenie mení lekciu, jej cenu aj odmeny blokov - obnoviť treba
      // všetko, čo z toho číta.
      await qc.invalidateQueries({ queryKey: ['changelog'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
      void qc.invalidateQueries({ queryKey: ['instructors'] })
      onReverted(result.detail, result.row_id)
      onClose()
    },
  })

  const label = (field: string) => t(fieldLabelKey(field))

  const value = (field: string, raw: unknown) => {
    // Náhľad porovnáva so **živým** záznamom, takže id sem chodia nepreložené -
    // na rozdiel od prehľadu zmien, kde ich preloží už server.
    const book = CODEBOOK[field]
    if (book && raw !== null && raw !== undefined && raw !== '') {
      const row = (boot?.[book] ?? []).find((entry) => entry.id === raw)
      if (row) return row.name || row.key || String(raw)
    }
    return formatValue(field, raw, { t, codebooks: boot, formatDateTime })
  }

  const blocked =
    (plan?.receipts.length ?? 0) > 0 || (plan?.paid_rewards ?? 0) > 0 || Boolean(plan?.order_paid)
  const impossible = plan?.action === 'none' || plan?.action === 'noop'

  return (
    <Modal open onClose={onClose} size="md" title={t('changelog.revert')}>
      {isLoading || !plan ? (
        <div className="flex h-24 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Čoho sa zmena týka - bez toho okno ukazuje len „Uhradené: V hotovosti
              → Nezaplatené“ a nie je jasné, o ktorú rezerváciu ide. */}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-slate-50 px-3 py-2 text-sm">
            <dt className="text-slate-500">{t(SUBJECT_LABEL[kind])}</dt>
            <dd className="font-medium">{plan.subject || '—'}</dd>
            {kind === 'client-reservations' || kind === 'instructor-reservations' ? (
              <>
                <dt className="text-slate-500">{t('changelog.reservationNumber')}</dt>
                <dd>{plan.subject_id}</dd>
              </>
            ) : null}
            {plan.context.start ? (
              <>
                <dt className="text-slate-500">{t('changelog.when')}</dt>
                <dd>{formatDateTime(plan.context.start)}</dd>
              </>
            ) : null}
            {plan.context.instructor ? (
              <>
                <dt className="text-slate-500">{t('reservation.instructor')}</dt>
                <dd>{plan.context.instructor}</dd>
              </>
            ) : null}
            <dt className="text-slate-500">{t('changelog.changedBy')}</dt>
            <dd>
              {plan.user_name || '—'}
              {plan.created ? <span className="text-slate-500">, {formatDateTime(plan.created)}</span> : null}
            </dd>
          </dl>

          {plan.receipts.length > 0 ? (
            <Alert variant="error">
              {t('changelog.revertReceipt', { receipts: plan.receipts.join(', ') })}
            </Alert>
          ) : null}

          {(plan.paid_rewards ?? 0) > 0 ? (
            <Alert variant="error">
              {t('changelog.revertPaidRewards', { count: plan.paid_rewards })}
            </Alert>
          ) : null}

          {impossible ? (
            <Alert variant="info">{t('changelog.revertNothing')}</Alert>
          ) : null}

          {/* Obnova nemá čo vypísať do tabuľky - záznam neexistuje, takže sa
              nie je s čím porovnávať. Bez tejto vety by okno ostalo prázdne. */}
          {plan.action === 'remove' ? (
            <Alert variant={plan.order_paid ? 'error' : 'warning'}>
              {plan.order_paid
                ? t('changelog.revertRemovePaid')
                : kind === 'instructor-reservations'
                  ? t('changelog.revertRemoveBlock')
                  : t('changelog.revertRemoveLesson')}
            </Alert>
          ) : null}

          {plan.action === 'restore' ? (
            <Alert variant="info">{t('changelog.revertRecreate')}</Alert>
          ) : null}

          {plan.fees_rebuilt ? (
            <Alert variant="info">{t('changelog.revertRestore')}</Alert>
          ) : null}

          {plan.lessons_stay_detached ? (
            <Alert variant="info">{t('changelog.revertBlockRestore')}</Alert>
          ) : null}

          {plan.later_changes > 0 ? (
            <Alert variant="warning">
              <span className="flex items-start gap-1.5">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {t('changelog.revertLater', { count: plan.later_changes })}
              </span>
            </Alert>
          ) : null}

          {plan.changes.length > 0 ? (
            <table className="table-base">
              <thead>
                <tr>
                  <th>{t('changelog.field')}</th>
                  <th>{t('changelog.valueNow')}</th>
                  <th>{t('changelog.valueAfter')}</th>
                </tr>
              </thead>
              <tbody>
                {plan.changes.map((change) => (
                  <tr key={change.field}>
                    <td>{label(change.field)}</td>
                    <td className="text-slate-500 line-through">
                      {value(change.field, change.from)}
                    </td>
                    <td className="font-medium">{value(change.field, change.to)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {revert.isError ? (
            <Alert variant="error">
              {apiErrorMessage(revert.error, t('changelog.revertFailed'))}
            </Alert>
          ) : null}

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={revert.isPending || blocked || impossible}
              onClick={() => revert.mutate()}
            >
              {revert.isPending ? <Spinner /> : null}
              {t('changelog.revertConfirm')}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('global.cancel')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
